import { appOrigin } from "./_origin";

/** Path IDs may start with `@` (Saloon). Keep it literal so URLs do not show `%40`. */
export function encodeSalonpagePathId(value: string | number): string {
  return encodeURIComponent(String(value)).replace(/%40/gi, "@");
}

export const salonjobsExternalUrl = (salonId: string | number) =>
  `${appOrigin("salonjobs")}/salons/${salonId}/`;

export const salonpageExternalUrl = (salonId: string | number) =>
  `${appOrigin("salonpage")}/salons/${encodeSalonpagePathId(salonId)}`;

export const salonpageMagazineRedirectUrl = (salonId: string | number) =>
  `${appOrigin("salonpage")}/redirect/magazine?hpb_salon_id=${encodeURIComponent(salonId)}`;

export const worldUrl = (salonId: string | number) =>
  `${appOrigin("world")}/salons/${salonId}/`;

export const hairbookSalonUrl = (salonId: string | number) =>
  `${appOrigin("hairbook")}/salons/${salonId}/`;

export const hairbookStyleThumbnailUrl = (styleId: string | number) =>
  `${appOrigin("hairbook")}/thumbnail/Style/${styleId}/`;

export const hairbookStaffThumbnailUrl = (staffId: string | number) =>
  `${appOrigin("hairbook")}/thumbnail/StaffProfile/${staffId}/`;

export const hairbookPostThumbnailUrl = (postId: string | number) =>
  `${appOrigin("hairbook")}/thumbnail/Post/${postId}/`;

export const hairbookGlobalImageUrl = (globalImageId: string | number) =>
  `${appOrigin("hairbook")}/photo/GlobalImage/${globalImageId}/`;

export const hairbookPostDownloadUrl = (postId: string | number) =>
  `${appOrigin("hairbook")}/posts/${postId}/download`;

export const hairbookPostHlsUrl = (postId: string | number) =>
  `https://blob.hairbook.jp/posts/${postId}/video.m3u8?v=20260619-cors`;

export const hairbookPostMp4Url = (postId: string | number) =>
  `https://blob.hairbook.jp/posts/${postId}/original.mp4`;

export const hairbookPostThumbnailR2Url = (postId: string | number) =>
  `https://blob.hairbook.jp/posts/${postId}/thumbnail.jpg`;

/** Console-uploaded magazine cover video. Play `source.mp4`, never `source.orig`. */
export const magazineHeroVideoMp4Url = (heroVideoId: string | number) =>
  `https://blob.hairbook.jp/magazine/videos/${heroVideoId}/source.mp4`;

export const magazineHeroVideoConversionUrl = (heroVideoId: string | number) =>
  `https://blob.hairbook.jp/magazine/videos/${heroVideoId}/conversion.json`;

export const salonpageVideoMp4Url = (videoId: string | number) =>
  `https://blob.hairbook.jp/videos/${videoId}/source.mp4`;

export const salonpageVideoPosterUrl = (videoId: string | number) =>
  `https://blob.hairbook.jp/videos/${videoId}/poster.webp`;

export const salonpageVideoThumbnailUrl = (videoId: string | number) =>
  `https://blob.hairbook.jp/videos/${videoId}/thumbnail.webp`;

export const salonJobVideoUrl = (videoId: string | number) =>
  `${appOrigin("hairbook")}/video/JobLpArticle/${videoId}/`;

export const salonJobPosterUrl = (videoId: string | number) =>
  `${appOrigin("hairbook")}/thumbnail/JobLpArticle/${videoId}/`;

export const salonJobR2VideoUrl = (videoId: string | number, hasHls = false) =>
  hasHls
    ? `https://blob.hairbook.jp/salonjobs/videos/${videoId}/hls/video.m3u8`
    : `https://blob.hairbook.jp/salonjobs/videos/${videoId}/original.mp4`;

export const salonJobR2PosterUrl = (videoId: string | number) =>
  `https://blob.hairbook.jp/salonjobs/videos/${videoId}/poster.webp`;

export type SalonJobVideoSource = {
  id?: string | number | null;
  video_id?: string | number | null;
  assetId?: string | number | null;
  asset_video_id?: string | number | null;
  sourceId?: string | number | null;
  source_article_id?: string | number | null;
  hasHls?: boolean | number | null;
  has_hls?: boolean | number | null;
};

const isEnabledFlag = (value: boolean | number | null | undefined): boolean =>
  value === true || value === 1;

const resolvedSalonJobAssetId = (video: SalonJobVideoSource): string | number | null =>
  video.assetId ?? video.asset_video_id ?? null;

const resolvedSalonJobSourceId = (video: SalonJobVideoSource): string | number =>
  video.sourceId ?? video.source_article_id ?? video.id ?? video.video_id ?? "";

export const salonJobResolvedVideoUrl = (video: SalonJobVideoSource) => {
  const assetId = resolvedSalonJobAssetId(video);
  if (assetId) return salonJobR2VideoUrl(assetId, isEnabledFlag(video.hasHls ?? video.has_hls));
  return salonJobVideoUrl(resolvedSalonJobSourceId(video));
};

export const salonJobResolvedPosterUrl = (video: SalonJobVideoSource) => {
  const assetId = resolvedSalonJobAssetId(video);
  if (assetId) return salonJobR2PosterUrl(assetId);
  return salonJobPosterUrl(resolvedSalonJobSourceId(video));
};

export const salonLogoUrl = (salonId: string | number) =>
  `${appOrigin("hairbook")}/logo/Salon/${salonId}/`;

export const salonGalleryUrl = (salonId: string | number, attachmentId: string | number) =>
  `${appOrigin("hairbook")}/gallery/Salon/${salonId}/${attachmentId}/`;

export const jobLpArticleImageUrl = (articleId: string | number) =>
  `${appOrigin("hairbook")}/thumbnail/JobLpArticle/${articleId}/`;

/** Hairbook 本体の旧 photo 経路。salonjobs 公開 LP の読み取り先ではない。 */
export const jobLpArticlePhotoUrl = (articleId: string | number) =>
  `${appOrigin("hairbook")}/photo/JobLpArticle/${articleId}/`;

export type SalonjobsArticleImageSize = "thumbnail" | "gallery" | "original";

/**
 * Cache key for salonjobs article images.
 * Unix seconds in UTC from `salonjobs_lp_articles.image_updated_at`.
 * The query changes only when that timestamp changes.
 * Null or blank omits the query.
 */
export function salonjobsArticleImageVersion(imageUpdatedAt: string | number | null | undefined): string | null {
  if (imageUpdatedAt == null) return null;
  const raw = String(imageUpdatedAt).trim();
  if (!raw) return null;
  if (/^\d{10}$/.test(raw)) return raw;
  if (/^\d{13}$/.test(raw)) return String(Math.floor(Number(raw) / 1000));

  const iso = salonjobsArticleImageIsoUtc(raw);
  if (iso) {
    const ms = Date.parse(iso);
    if (!Number.isNaN(ms)) return String(Math.floor(ms / 1000));
  }

  const compact = raw.replace(/[^0-9A-Za-z]/g, "");
  return compact || null;
}

function salonjobsArticleImageIsoUtc(raw: string): string | null {
  const match = raw.match(
    /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?$/,
  );
  if (!match) return null;
  const [, date, hour, minute, second, fraction, zone] = match;
  if (!hour) return `${date}T00:00:00Z`;
  const normalizedZone = zone ? zone.replace(/^([+-]\d{2})(\d{2})$/, "$1:$2") : "Z";
  return `${date}T${hour}:${minute}:${second ?? "00"}${fraction ?? ""}${normalizedZone}`;
}

export const salonjobsArticleImageUrl = (
  articleId: string | number,
  size: SalonjobsArticleImageSize = "gallery",
  imageUpdatedAt?: string | number | null,
) => {
  const base = `https://blob.hairbook.jp/salonjobs/articles/${articleId}/${size}.webp`;
  const version = salonjobsArticleImageVersion(imageUpdatedAt);
  return version ? `${base}?v=${version}` : base;
};

export type SalonpageImageSize = "thumbnail" | "gallery" | "original";

export const salonpageR2ImageUrl = (
  salonId: string | number,
  imageId: string,
  size: SalonpageImageSize = "gallery",
) =>
  `https://blob.hairbook.jp/salonpages/${salonId}/images/${imageId}/${size}.webp`;

export const salonpageR2MenuImageUrl = (
  salonId: string | number,
  menuId: string,
  size: SalonpageImageSize = "gallery",
) =>
  `https://blob.hairbook.jp/salonpages/${salonId}/menus/${menuId}/${size}.webp`;

export const salonpageR2LogoUrl = (
  salonId: string | number,
  size: SalonpageImageSize = "thumbnail",
) =>
  `https://blob.hairbook.jp/salonpages/${salonId}/logo/${size}.webp`;

export const salonpageStaffVideoUrl = (
  salonId: string | number,
  staffId: string | number,
) =>
  `https://blob.hairbook.jp/salonpages/${salonId}/staff-videos/${staffId}/profile.mp4`;
